import React, { useState, createContext, useContext, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { BaseCustomProps } from './types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { BarChart3, TrendingUp, TrendingDown, Users, DollarSign, Activity, AlertCircle, CheckCircle, Info, Clock, Send, BarChart, LineChart } from 'lucide-react';

// Define the structure for dynamic UI sections
interface UISection {
  type: 'header' | 'text' | 'metric' | 'chart' | 'button' | 'card' | 'grid' | 'list' | 'progress' | 'alert' | 'separator' | 'badge-group' | 'input' | 'textarea' | 'select' | 'checkbox' | 'submit';
  id?: string;
  title?: string;
  text?: string;
  content?: string;
  value?: string | number;
  label?: string;
  trend?: string;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray' | 'orange';
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  items?: UISection[];
  data?: any[];
  columns?: number;
  action?: string;
  href?: string;
  percentage?: number;
  alertType?: 'info' | 'warning' | 'error' | 'success';
  badges?: { text: string; color?: string }[];
  // Form-specific properties
  name?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  defaultValue?: string | number | boolean;
  options?: { label: string; value: string }[];
  // Chart-specific properties
  chartType?: 'bar' | 'line' | 'pie';
  chartData?: Array<{ label: string; value: number; color?: string }>;
  showLabels?: boolean;
  showValues?: boolean;
}

interface DynamicUIProps extends BaseCustomProps {
  layout?: 'card' | 'dashboard' | 'form' | 'list' | 'grid' | 'report' | string | Record<string, unknown>;
  sections?: UISection[] | string;
  theme?: 'modern' | 'minimal' | 'classic';
  title?: string;
}

// Form Data Context for handling form state and submission
interface FormData {
  [key: string]: string | number | boolean;
}

interface FormContextType {
  formData: FormData;
  updateFormData: (name: string, value: string | number | boolean) => void;
  submitForm: () => void;
  isSubmitted: boolean;
}

const FormContext = createContext<FormContextType | null>(null);

const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a DynamicUI component');
  }
  return context;
};

// Custom hook to inject form data into next user message
const useFormDataInjection = () => {
  // This hook would integrate with the chat input system
  // For now, we'll use localStorage as a simple mechanism
  const injectFormData = (formData: FormData) => {
    if (Object.keys(formData).length === 0) return;
    
    const formDataString = Object.entries(formData)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    
    // Store form data to be prepended to next message
    localStorage.setItem('dynamicui_form_data', `Form Data: [${formDataString}]\n\n`);
    
    // Also trigger a custom event that ChatInput can listen to
    window.dispatchEvent(new CustomEvent('dynamicui_form_submitted', {
      detail: { formData, formDataString: `Form Data: [${formDataString}]\n\n` }
    }));
  };
  
  return { injectFormData };
};

// Simple Chart Components
const SimpleBarChart: React.FC<{ data: Array<{ label: string; value: number; color?: string }>; showLabels?: boolean; showValues?: boolean }> = ({ 
  data, showLabels = true, showValues = true 
}) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];
  
  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={index} className="space-y-1">
          {showLabels && <div className="text-sm text-gray-600">{item.label}</div>}
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${(item.value / maxValue) * 100}%`,
                  backgroundColor: item.color || colors[index % colors.length]
                }}
              />
            </div>
            {showValues && <span className="text-sm font-medium text-gray-700 min-w-[3rem] text-right">{item.value}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

const SimpleLineChart: React.FC<{ data: Array<{ label: string; value: number; color?: string }>; showLabels?: boolean; showValues?: boolean }> = ({ 
  data, showLabels = true, showValues = true 
}) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;
  const width = 300;
  const height = 120;
  const padding = 20;
  
  const points = data.map((item, index) => {
    const x = padding + (index / (data.length - 1)) * (width - 2 * padding);
    const y = height - padding - ((item.value - minValue) / range) * (height - 2 * padding);
    return { x, y, ...item };
  });
  
  const pathData = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
  
  return (
    <div className="space-y-3">
      <svg width={width} height={height} className="border border-gray-200 rounded bg-gray-50">
        <polyline
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          points={points.map(p => `${p.x},${p.y}`).join(' ')}
        />
        {points.map((point, index) => (
          <circle key={index} cx={point.x} cy={point.y} r="3" fill="#3b82f6" />
        ))}
      </svg>
      {showLabels && (
        <div className="flex justify-between text-xs text-gray-600">
          {data.map((item, index) => (
            <span key={index}>{item.label}</span>
          ))}
        </div>
      )}
      {showValues && (
        <div className="flex justify-between text-xs text-gray-700">
          {data.map((item, index) => (
            <span key={index} className="font-medium">{item.value}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const SimplePieChart: React.FC<{ data: Array<{ label: string; value: number; color?: string }>; showLabels?: boolean; showValues?: boolean }> = ({ 
  data, showLabels = true, showValues = true 
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];
  
  let cumulativePercentage = 0;
  const segments = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const startAngle = cumulativePercentage * 3.6; // Convert to degrees
    cumulativePercentage += percentage;
    const endAngle = cumulativePercentage * 3.6;
    
    return {
      ...item,
      percentage,
      startAngle,
      endAngle,
      color: item.color || colors[index % colors.length]
    };
  });
  
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 42 42" className="w-full h-full">
          <circle
            cx="21"
            cy="21"
            r="15.915"
            fill="transparent"
            stroke="#e5e7eb"
            strokeWidth="3"
          />
          {segments.map((segment, index) => {
            const strokeDasharray = `${segment.percentage} ${100 - segment.percentage}`;
            const strokeDashoffset = -segments.slice(0, index).reduce((sum, s) => sum + s.percentage, 0);
            
            return (
              <circle
                key={index}
                cx="21"
                cy="21"
                r="15.915"
                fill="transparent"
                stroke={segment.color}
                strokeWidth="3"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
              />
            );
          })}
        </svg>
      </div>
      {(showLabels || showValues) && (
        <div className="space-y-1">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: segment.color }}
              />
              {showLabels && <span className="text-gray-600">{segment.label}</span>}
              {showValues && <span className="font-medium text-gray-700">({segment.value})</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component renderers for each section type
const SectionRenderer: React.FC<{ section: UISection; theme: string }> = ({ section, theme }) => {
  // Move useFormContext to top level to avoid conditional hook calls
  const formContext = useContext(FormContext);
  
  const getColorClasses = (color: string = 'blue') => {
    const colorMap = {
      blue: 'text-blue-600 bg-blue-50 border-blue-200',
      green: 'text-green-600 bg-green-50 border-green-200',
      red: 'text-red-600 bg-red-50 border-red-200',
      yellow: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      purple: 'text-purple-600 bg-purple-50 border-purple-200',
      gray: 'text-gray-600 bg-gray-50 border-gray-200',
      orange: 'text-orange-600 bg-orange-50 border-orange-200'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getTrendIcon = (trend?: string) => {
    if (trend?.startsWith('+')) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (trend?.startsWith('-')) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };

  const getMetricIcon = (label?: string) => {
    if (label?.toLowerCase().includes('revenue') || label?.toLowerCase().includes('sales')) {
      return <DollarSign className="h-5 w-5" />;
    }
    if (label?.toLowerCase().includes('users') || label?.toLowerCase().includes('visitors')) {
      return <Users className="h-5 w-5" />;
    }
    if (label?.toLowerCase().includes('chart') || label?.toLowerCase().includes('data')) {
      return <BarChart3 className="h-5 w-5" />;
    }
    return <Activity className="h-5 w-5" />;
  };

  const getAlertIcon = (alertType?: string) => {
    switch (alertType) {
      case 'error': return <AlertCircle className="h-4 w-4" />;
      case 'success': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <Clock className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  switch (section.type) {
    case 'header':
      const HeaderTag = section.size === 'large' ? 'h1' : section.size === 'small' ? 'h3' : 'h2';
      return (
        <HeaderTag className={twMerge(
          'font-bold',
          section.size === 'large' ? 'text-3xl' : section.size === 'small' ? 'text-lg' : 'text-2xl',
          getColorClasses(section.color).split(' ')[0]
        )}>
          {section.title || section.text}
        </HeaderTag>
      );

    case 'text':
      return (
        <p className={twMerge(
          'text-gray-700',
          section.size === 'large' ? 'text-lg' : section.size === 'small' ? 'text-sm' : 'text-base'
        )}>
          {section.content || section.text}
        </p>
      );

    case 'metric':
      return (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{section.label}</p>
              <p className="text-2xl font-bold">{section.value}</p>
              {section.trend && (
                <div className="flex items-center gap-1 mt-1">
                  {getTrendIcon(section.trend)}
                  <span className={twMerge(
                    'text-sm',
                    section.trend?.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  )}>
                    {section.trend}
                  </span>
                </div>
              )}
            </div>
            <div className={`p-2 rounded-lg ${getColorClasses(section.color)}`}>
              {getMetricIcon(section.label)}
            </div>
          </div>
        </Card>
      );

    case 'chart':
      // Default chart data if none provided
      const defaultChartData = [
        { label: 'Jan', value: 20 },
        { label: 'Feb', value: 45 },
        { label: 'Mar', value: 30 },
        { label: 'Apr', value: 60 },
        { label: 'May', value: 75 }
      ];
      
      const chartData = section.chartData || section.data || defaultChartData;
      const chartType = section.chartType || 'bar';
      
      const renderChart = () => {
        switch (chartType) {
          case 'line':
            return <SimpleLineChart data={chartData} showLabels={section.showLabels} showValues={section.showValues} />;
          case 'pie':
            return <SimplePieChart data={chartData} showLabels={section.showLabels} showValues={section.showValues} />;
          default:
            return <SimpleBarChart data={chartData} showLabels={section.showLabels} showValues={section.showValues} />;
        }
      };
      
      const chartIcon = chartType === 'line' ? LineChart : chartType === 'pie' ? BarChart : BarChart3;
      const ChartIcon = chartIcon;
      
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ChartIcon className="h-5 w-5" />
              {section.title || `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderChart()}
            {section.content && <p className="mt-3 text-sm text-gray-600">{section.content}</p>}
          </CardContent>
        </Card>
      );

    case 'button':
      return (
        <Button 
          variant={section.variant as any} 
          size={section.size as any}
          className={section.color ? getColorClasses(section.color) : ''}
        >
          {section.text || section.label || 'Action'}
        </Button>
      );

    case 'card':
      return (
        <Card>
          {section.title && (
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
            </CardHeader>
          )}
          <CardContent>
            {section.content || section.text}
            {section.items && (
              <div className="space-y-2 mt-3">
                {section.items.map((item, idx) => (
                  <SectionRenderer key={idx} section={item} theme={theme} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      );

    case 'grid':
      return (
        <div className={`grid gap-4 grid-cols-${section.columns || 2}`}>
          {section.items?.map((item, idx) => (
            <SectionRenderer key={idx} section={item} theme={theme} />
          ))}
        </div>
      );

    case 'list':
      return (
        <div className="space-y-2">
          {section.items?.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span>{item.text || item.content}</span>
            </div>
          ))}
        </div>
      );

    case 'progress':
      return (
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm">{section.label}</span>
            <span className="text-sm text-muted-foreground">{section.percentage}%</span>
          </div>
          <Progress value={section.percentage || 0} className="h-2" />
        </div>
      );

    case 'alert':
      return (
        <Alert className={twMerge(
          'flex items-center gap-2',
          section.alertType === 'error' && 'border-red-200 bg-red-50',
          section.alertType === 'success' && 'border-green-200 bg-green-50',
          section.alertType === 'warning' && 'border-yellow-200 bg-yellow-50'
        )}>
          {getAlertIcon(section.alertType)}
          <span>{section.text || section.content}</span>
        </Alert>
      );

    case 'separator':
      return <Separator className="my-4" />;

    case 'badge-group':
      return (
        <div className="flex flex-wrap gap-2">
          {section.badges?.map((badge, idx) => (
            <Badge key={idx} variant={badge.color as any || 'default'}>
              {badge.text}
            </Badge>
          ))}
        </div>
      );

    case 'input':
      if (!formContext) {
        return <div className="text-red-500">Form context not available</div>;
      }
      const inputValue = formContext.formData[section.name || section.id || ''] || section.defaultValue || '';
      
      return (
        <div className="space-y-1">
          {section.label && (
            <label className="text-sm font-medium text-gray-700">
              {section.label}
              {section.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <Input
            type="text"
            name={section.name || section.id}
            value={inputValue as string}
            placeholder={section.placeholder}
            disabled={section.disabled || formContext.isSubmitted}
            required={section.required}
            onChange={(e) => {
              if (section.name || section.id) {
                formContext.updateFormData(section.name || section.id!, e.target.value);
              }
            }}
            className={section.color ? getColorClasses(section.color) : ''}
          />
        </div>
      );

    case 'textarea':
      if (!formContext) {
        return <div className="text-red-500">Form context not available</div>;
      }
      const textareaValue = formContext.formData[section.name || section.id || ''] || section.defaultValue || '';
      
      return (
        <div className="space-y-1">
          {section.label && (
            <label className="text-sm font-medium text-gray-700">
              {section.label}
              {section.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <Textarea
            name={section.name || section.id}
            value={textareaValue as string}
            placeholder={section.placeholder}
            disabled={section.disabled || formContext.isSubmitted}
            required={section.required}
            onChange={(e) => {
              if (section.name || section.id) {
                formContext.updateFormData(section.name || section.id!, e.target.value);
              }
            }}
            className={twMerge('min-h-[80px]', section.color ? getColorClasses(section.color) : '')}
          />
        </div>
      );

    case 'select':
      if (!formContext) {
        return <div className="text-red-500">Form context not available</div>;
      }
      const selectValue = formContext.formData[section.name || section.id || ''] || section.defaultValue || '';
      
      return (
        <div className="space-y-1">
          {section.label && (
            <label className="text-sm font-medium text-gray-700">
              {section.label}
              {section.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <Select
            value={selectValue as string}
            onValueChange={(value) => {
              if (section.name || section.id) {
                formContext.updateFormData(section.name || section.id!, value);
              }
            }}
            disabled={section.disabled || formContext.isSubmitted}
          >
            <SelectTrigger className={section.color ? getColorClasses(section.color) : ''}>
              <SelectValue placeholder={section.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {section.options?.map((option, index) => (
                <SelectItem key={index} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'checkbox':
      if (!formContext) {
        return <div className="text-red-500">Form context not available</div>;
      }
      const checkboxValue = formContext.formData[section.name || section.id || ''] || section.defaultValue || false;
      
      return (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={section.name || section.id}
            checked={checkboxValue as boolean}
            disabled={section.disabled || formContext.isSubmitted}
            onCheckedChange={(checked) => {
              if (section.name || section.id) {
                formContext.updateFormData(section.name || section.id!, checked as boolean);
              }
            }}
          />
          {section.label && (
            <label 
              htmlFor={section.name || section.id}
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              {section.label}
              {section.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
        </div>
      );

    case 'submit':
      if (!formContext) {
        return <div className="text-red-500">Form context not available</div>;
      }
      
      return (
        <Button 
          onClick={formContext.submitForm}
          disabled={formContext.isSubmitted}
          variant={section.variant as any}
          size={section.size as any}
          className={twMerge(
            'flex items-center gap-2',
            section.color ? getColorClasses(section.color) : 'bg-blue-600 hover:bg-blue-700 text-white',
            formContext.isSubmitted && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Send className="h-4 w-4" />
          {formContext.isSubmitted ? 'Submitted' : (section.text || section.label || 'Submit')}
        </Button>
      );

    default:
      return <div className="text-red-500">Unknown section type: {section.type}</div>;
  }
};

export const DynamicUI = ({ 
  layout = 'card',
  sections,
  theme = 'modern',
  title,
  className,
  children 
}: DynamicUIProps) => {
  // Form data state
  const [formData, setFormData] = useState<FormData>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { injectFormData } = useFormDataInjection();

  // Parse sections if they're passed as a string
  let parsedSections: UISection[] = [];
  
  if (typeof sections === 'string') {
    try {
      parsedSections = JSON.parse(sections);
    } catch (error) {
      console.error('Failed to parse sections:', error);
      return <div className="text-red-500 p-4">Error: Invalid sections format</div>;
    }
  } else if (Array.isArray(sections)) {
    parsedSections = sections;
  } else if (children) {
    // If no sections provided, render children
    return <div className={twMerge('dynamic-ui', className as string)}>{children}</div>;
  }

  // Form context functions
  const updateFormData = (name: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const submitForm = () => {
    setIsSubmitted(true);
    injectFormData(formData);
    
    // Show success message briefly
    setTimeout(() => {
      setIsSubmitted(false);
    }, 3000);
  };

  const formContextValue: FormContextType = {
    formData,
    updateFormData,
    submitForm,
    isSubmitted
  };

  const getLayoutClasses = () => {
    switch (layout) {
      case 'dashboard':
        return 'space-y-6 p-6 bg-gray-50 rounded-lg';
      case 'form':
        return 'space-y-4 p-4 border rounded-lg';
      case 'list':
        return 'space-y-2';
      case 'grid':
        return 'grid gap-4';
      case 'report':
        return 'space-y-8 p-6';
      default:
        return 'space-y-4 p-4 border rounded-lg bg-white';
    }
  };

  const containerClasses = twMerge(
    'dynamic-ui',
    getLayoutClasses(),
    theme === 'minimal' && 'border-none p-2',
    theme === 'classic' && 'border-2 border-gray-300',
    className as string
  );

  return (
    <FormContext.Provider value={formContextValue}>
      <div className={containerClasses}>
        {title && (
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <Separator className="mt-2" />
          </div>
        )}
        
        {parsedSections.map((section, index) => (
          <div key={section.id || index}>
            <SectionRenderer section={section} theme={theme} />
          </div>
        ))}
        
        {children && <div className="mt-4">{children as ReactNode}</div>}
        
        {/* Show form submission status */}
        {isSubmitted && (
          <Alert className="mt-4 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-green-800">Form data has been prepared for your next message!</span>
          </Alert>
        )}
      </div>
    </FormContext.Provider>
  );
};

export default DynamicUI;